import React, { useContext, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';
import { ConfigContext } from "../../Config/ConfigContext";
import useApi from '../../hooks/useApi';

import TextField from '@mui/material/TextField';
import { useParams } from 'react-router-dom';
import {
	Box,
	Button,
	Chip,
	Container,
	Stack,
	Typography,
	Checkbox,
	FormControlLabel
} from '@mui/material';
import { camelCase } from 'lodash';

/**
 * Loads email template data.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.values - The values object containing configuration and emailTemplateId.
 * @param {Object} props.api - The API methods.
 * @returns {Promise<Object>} A promise that resolves to the merged values including loaded email template data.
 */
const onLoad = async ({ values, api }) => {
	const emailTemplate = await api.emailTemplates.getById({ id: values.emailTemplateId });
	return {
		...values,
		...emailTemplate
	};
};

/**
 * EmailTemplateDetail component.
 * Renders and manages the details of an email template.
 *
 * @component
 * @returns {JSX.Element} The email template detail view.
 */
export default function EmailTemplateDetail() {
	const { emailTemplateId } = useParams();
	const { config } = useContext(ConfigContext);
	const api = useApi();

	const [form, setForm] = useState({
		body: "",
		subject: "",
		from: "",
		emailTemplateId,
		name: "",
		system: false,
		config
	});

	// Placeholder for errors; in a production app, validation errors can be managed here.
	const errors = {};

	/**
	 * Handles the change for form fields.
	 *
	 * @param {any} value - The new value.
	 * @param {string} field - The field name to update.
	 */
	const handleChange = (value, field) => {
		setForm(prev => ({ ...prev, [field]: value }));
	};

	// Load initial data using the useApi hook.
	useEffect(() => {
		const loadData = async () => {
			const result = await onLoad({ values: form, api });
			setForm(result);
		};
		loadData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Extract replacement variables from the email body.
	const replacementArray = [...form.body.matchAll(/{(.*?)}/g)].reduce((previous, current) => {
		(previous[current[0]] = previous[current[0]] || []).push(current);
		return previous;
	}, {});

	const editorRef = useRef(null);

	return (
		<Container>
			<Stack direction={'row'} spacing={2}>
				{/* Template Name Input */}
				<TextField
					label="Template Name"
					value={form.name}
					error={Boolean(errors.name)}
					helperText={errors.name}
					onChange={(e) => {
						const newValue = camelCase(e.target.value);
						handleChange(newValue, 'name');
					}}
					fullWidth
				/>

				{/* Email Subject Input */}
				<TextField
					label="Email Subject"
					value={form.subject}
					error={Boolean(errors.subject)}
					helperText={errors.subject}
					onChange={(e) => handleChange(e.target.value, 'subject')}
					fullWidth
				/>

				{/* From Address Input */}
				<TextField
					label="From Address"
					value={form.from}
					error={Boolean(errors.from)}
					helperText={errors.from}
					onChange={(e) => handleChange(e.target.value, 'from')}
					fullWidth
				/>

				{/* Is System Checkbox */}
				<FormControlLabel
					control={
						<Checkbox
							checked={form.system}
							onChange={(e) => handleChange(e.target.checked, 'system')}
							name="system"
							color="primary"
						/>
					}
					label="Is System"
				/>
			</Stack>

			{/* Replacement Variables Display */}
			<Box sx={{ marginTop: '10px', marginBottom: '10px' }}>
				<Typography variant="h6">
					Replacement Variables
				</Typography>
				<Stack direction="row" spacing={1} flexWrap="wrap">
					{Object.keys(replacementArray).map((key, index) => {
						const replacement = replacementArray[key];
						return (
							<Chip key={index} label={`${key} x${replacement.length}`} variant="outlined" />
						);
					})}
				</Stack>
			</Box>

			{/* Save Button */}
			<Button
				sx={{ marginTop: '10px', marginBottom: '10px' }}
				variant="contained"
				color="primary"
				onClick={async () => {
					try {
						await api.emailTemplates.update({ 
							id: emailTemplateId, 
							data: {
								body: form.body,
								subject: form.subject,
								from: form.from,
								name: form.name,
								system: form.system
							} 
						});
						// Optionally, you can add a success message or redirect here.
					} catch (error) {
						console.error("Failed to save email template:", error);
					}
				}}
			>
				Save
			</Button>

			{/* TinyMCE Editor for Email Body */}
			<Editor
				apiKey={config.wysiwyg.apiKey}
				onInit={(evt, editor) => editorRef.current = editor}
				value={form.body}
				onEditorChange={(value) => {
					handleChange(value, 'body');
				}}
				init={{
					height: 750,
					menubar: false,
					plugins: [
						'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
						'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
						'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
					],
					toolbar: 'image | code | link | undo redo | blocks | ' +
						'bold italic forecolor | alignleft aligncenter ' +
						'alignright alignjustify | bullist numlist outdent indent | ' +
						'removeformat | help',
					content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
					relative_urls: false,
					remove_script_host: false,
					convert_urls: false
				}}
			/>
		</Container>
	);
}

EmailTemplateDetail.propTypes = {};
